import { useEffect, useRef, useState } from 'react';
import { KeyRound, X, ArrowLeft, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { listQuickSignins, removeQuickSignin } from '../lib/quickSignin';
import { Input } from './ui/input';
import { Button } from './ui/button';

const initials = (s = '') => s.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

const StaffTile = ({ cred, onPick, onRemove }) => (
  <div className="group relative">
    <button type="button" onClick={() => onPick(cred)} data-testid={`quick-tile-${cred.cred_id}`}
      className="w-full flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5 text-left transition-colors hover:border-primary/50 hover:bg-primary/5">
      <span className="h-9 w-9 shrink-0 rounded-full bg-primary/10 text-primary text-sm font-semibold flex items-center justify-center">
        {initials(cred.name || cred.email)}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium truncate">{cred.name || cred.email}</span>
        <span className="block text-xs text-muted-foreground truncate">{cred.email}</span>
      </span>
    </button>
    <button type="button" title="Remove from this computer" onClick={() => onRemove(cred)}
      data-testid={`quick-remove-${cred.cred_id}`}
      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100 focus:opacity-100">
      <X className="h-3.5 w-3.5" />
    </button>
  </div>
);

export const QuickPinSignIn = ({ onSignedIn, onUsePassword, onEmpty }) => {
  const { quickLogin } = useAuth();
  const [creds, setCreds] = useState(null);
  const [picked, setPicked] = useState(null);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pinRef = useRef(null);

  useEffect(() => { listQuickSignins().then(setCreds); }, []);
  useEffect(() => { if (creds && !creds.length) onEmpty(); }, [creds, onEmpty]);
  useEffect(() => { if (picked) pinRef.current?.focus(); }, [picked]);

  const drop = async (cred) => {
    await removeQuickSignin(cred.cred_id);
    setCreds((c) => c.filter((x) => x.cred_id !== cred.cred_id));
    if (picked?.cred_id === cred.cred_id) setPicked(null);
  };

  const submit = async (value) => {
    setBusy(true); setError('');
    try {
      const userData = await quickLogin(picked, value);
      await onSignedIn(userData, picked.email);
    } catch (err) {
      const status = err.response?.status;
      setError(err.response?.data?.detail || 'Could not sign in');
      setPin('');
      if (status === 429 || (status === 401 && !/Wrong PIN/.test(err.response?.data?.detail || ''))) {
        await drop(picked);
        onUsePassword(picked.email);
      }
    }
    setBusy(false);
  };

  const onPin = (v) => {
    const digits = v.replace(/\D/g, '').slice(0, 4);
    setPin(digits);
    if (digits.length === 4) submit(digits);
  };

  if (!creds?.length) return null;

  if (picked) {
    return (
      <div className="space-y-3" data-testid="quick-pin-step">
        <button type="button" onClick={() => { setPicked(null); setPin(''); setError(''); }}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="quick-pin-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Choose someone else
        </button>
        <p className="text-sm">Hi <b>{picked.name || picked.email}</b> — enter your 4-digit PIN</p>
        <Input ref={pinRef} type="password" inputMode="numeric" autoComplete="off" maxLength={4} value={pin}
          onChange={(e) => onPin(e.target.value)} disabled={busy} placeholder="••••"
          className="h-12 text-center text-2xl tracking-[0.6em] font-mono" data-testid="quick-pin-input" />
        {busy && <p className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Signing in…</p>}
        {error && <p role="alert" className="text-xs font-medium text-destructive" data-testid="quick-pin-error">{error}</p>}
        <Button type="button" variant="ghost" size="sm" className="w-full" onClick={() => onUsePassword(picked.email)}
          data-testid="quick-use-password-btn">Use my password instead</Button>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="quick-signin-list">
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <KeyRound className="h-3.5 w-3.5" /> Sign in with your PIN
      </p>
      {creds.map((c) => <StaffTile key={c.cred_id} cred={c} onPick={setPicked} onRemove={drop} />)}
      <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => onUsePassword('')}
        data-testid="quick-someone-else-btn">Someone else — use email and password</Button>
    </div>
  );
};
